import { registerBlock, registerRelationship } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const couplesOptimization: ContentBlock<"program"> = {
  id: makeId("program-couples-optimization"),
  kind: "program",
  tags: [
    "optimization",
    "both-partners",
    "couples",
    "supplements",
    "lifestyle",
    "pre-cycle",
  ],
  title: "Couples Optimization",
  subtitle: "Both partners optimizing together for 90 days",
  icon: "💑",
  body: {
    duration: "3 months",
    includes: [
      "Fertility baseline for her (hormone panel, ultrasound)",
      "Semen analysis for him",
      "Targeted supplement protocols for both partners",
      "Lifestyle guidance for both partners",
      "Monthly check-ins for both",
      "90-day retesting for both (labs for her, SA for him)",
    ],
    priceRange: { low: 600, high: 1200 },
    currency: "USD",
    status: "coming-soon",
    forWhom: "both",
    category: "optimization",
    moduleIds: [
      makeId("module-female-hormone-baseline"),
      makeId("module-male-fertility-baseline"),
      makeId("module-female-optimization"),
      makeId("module-male-optimization"),
    ],
    phases: [
      {
        title: "Baseline",
        description: "Both partners assessed in parallel",
        moduleIds: [
          makeId("module-female-hormone-baseline"),
          makeId("module-male-fertility-baseline"),
        ],
        parallel: true,
      },
      {
        title: "Optimize together",
        description: "90 days of supplements and lifestyle changes for both",
        moduleIds: [
          makeId("module-female-optimization"),
          makeId("module-male-optimization"),
        ],
        parallel: true,
      },
    ],
    steps: [
      {
        title: "Both assessed",
        description:
          "Hormone panel + ultrasound for her, semen analysis for him.",
      },
      {
        title: "Parallel protocols",
        description:
          "Both start tailored supplement and lifestyle protocols simultaneously.",
      },
      {
        title: "Retest together",
        description:
          "Both retest at 90 days to measure progress before treatment decisions.",
      },
    ],
  },
  sources: [
    {
      title: "Coenzyme Q10 supplementation and oocyte quality",
      journal: "Reproductive BioMedicine Online",
      year: 2021,
      evidenceLevel: "rct",
    },
    {
      title: "The effect of antioxidant supplementation on sperm parameters",
      journal: "Reproductive BioMedicine Online",
      year: 2022,
      evidenceLevel: "meta-analysis",
    },
  ],
};

registerBlock(couplesOptimization);

registerRelationship({
  from: makeId("program-couples-optimization"),
  to: makeId("module-female-hormone-baseline"),
  type: "contains",
});
registerRelationship({
  from: makeId("program-couples-optimization"),
  to: makeId("module-male-fertility-baseline"),
  type: "contains",
});
registerRelationship({
  from: makeId("program-couples-optimization"),
  to: makeId("module-female-optimization"),
  type: "contains",
});
registerRelationship({
  from: makeId("program-couples-optimization"),
  to: makeId("module-male-optimization"),
  type: "contains",
});
